/**
 * <ThreadSwitcher /> — the chat panel's thread dropdown (M25.4).
 *
 * Lets the user run multiple parallel investigations: switch
 * between open conversations, start a fresh one, or reopen a pinned
 * thread (M25.3). The pinned list is fetched lazily when the
 * dropdown opens.
 */

import { useQuery } from "@tanstack/react-query";
import { Bookmark, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";

export interface OpenConversation {
  id: string;
  title: string;
}

export interface ThreadSwitcherProps {
  conversations: OpenConversation[];
  activeId: string;
  onSelect: (conversationId: string) => void;
  onNew: () => void;
  onOpenPinned: (threadId: string) => void;
}

export function ThreadSwitcher({
  conversations,
  activeId,
  onSelect,
  onNew,
  onOpenPinned,
}: ThreadSwitcherProps) {
  const { t: tx } = useTranslation();
  const [open, setOpen] = useState(false);

  const pinned = useQuery({
    queryKey: ["chat-threads"],
    queryFn: () => api.listChatThreads(),
    enabled: open,
  });

  const active =
    conversations.find((c) => c.id === activeId) ?? conversations[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex max-w-[160px] items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
      >
        <span className="truncate">{active?.title ?? tx("app.chat_ask_solvait")}</span>
        <ChevronDown size={12} className="shrink-0" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute z-50 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border border-border-subtle bg-surface-overlay py-1 shadow-lg"
          >
            {/* Open conversations */}
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
              Open
            </p>
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  onSelect(c.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center px-3 py-1.5 text-start text-xs hover:bg-surface-sunken ${
                  c.id === activeId
                    ? "font-semibold text-text-primary"
                    : "text-text-secondary"
                }`}
              >
                <span className="truncate">{c.title}</span>
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onNew();
                setOpen(false);
              }}
              className="flex w-full items-center gap-1.5 px-3 py-1.5 text-start text-xs font-medium text-brand-blue hover:bg-surface-sunken"
            >
              <Plus size={12} /> New chat
            </button>

            {/* Pinned threads */}
            <div className="my-1 border-t border-border-subtle" />
            <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
              Saved threads
            </p>
            {pinned.isLoading && (
              <p className="px-3 py-1.5 text-xs text-text-tertiary">
                {tx("app.setup_loading_dots")}
              </p>
            )}
            {pinned.data && pinned.data.length === 0 && (
              <p className="px-3 py-1.5 text-xs text-text-tertiary">
                {tx("app.ts_no_threads")}
              </p>
            )}
            {pinned.data?.map((t) => (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  onOpenPinned(t.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-1.5 px-3 py-1.5 text-start text-xs text-text-secondary hover:bg-surface-sunken"
              >
                <Bookmark size={12} className="shrink-0" />
                <span className="truncate">{t.name}</span>
                <span className="ms-auto shrink-0 text-xs text-text-tertiary">
                  {t.message_count}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
